import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import keycloak from './keycloak';
import './index.css';
import App from './App';
import store from './store';

// Rehydrate stored tokens (if present) to avoid forced redirect on refresh.
const storedToken = localStorage.getItem('kc_token');
const storedRefreshToken = localStorage.getItem('kc_refreshToken');
const storedIdToken = localStorage.getItem('kc_idToken');
const hasStoredToken = !!storedToken && !!storedRefreshToken;

/*
 * Silent SSO caveat (Keycloak + modern browsers):
 * Browsers in incognito/private mode or with third‑party cookies blocked will prevent the hidden iframe
 * that Keycloak uses for 'check-sso' from accessing the Keycloak session cookie. This yields warnings like:
 *   "Your browser is blocking access to 3rd-party cookies ... silent authentication not possible"
 * After a failure we persist `kc_disableCheckSso` to avoid repeating the attempt on every reload, falling back
 * to a normal interactive login flow. To re-enable silent SSO, clear this flag from localStorage manually.
 */
const disableCheckSso = localStorage.getItem('kc_disableCheckSso') === 'true';

// Public routes that must render without forcing a Keycloak login redirect.
const PUBLIC_PATHS = ['/reset-password'];
const isPublicRoute = PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));

// Use check-sso when we have previously stored tokens so we attempt silent SSO / token reuse.
// Skip if a prior attempt indicated third-party cookies are blocked (incognito / cross-site cookie restrictions).
// Always include the stored tokens in init options; Keycloak will validate / refresh them if still valid.
// For public routes (e.g. password reset) use check-sso so the page renders without forcing login.
const initOptions = {
  onLoad: isPublicRoute ? 'check-sso' : (hasStoredToken && !disableCheckSso ? 'check-sso' : 'login-required'),
  pkceMethod: 'S256',
  silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
  checkLoginIframe: false,
  // Add timeout to prevent hanging on invalid tokens
  silentCheckSsoFallback: true,
  enableLogging: true,
  token: storedToken || undefined,
  refreshToken: storedRefreshToken || undefined,
  idToken: storedIdToken || undefined
};

// Persist tokens whenever they change.
const handleTokens = (tokens) => {
  if (!tokens) return;
  if (tokens.token) localStorage.setItem('kc_token', tokens.token);
  if (tokens.idToken) localStorage.setItem('kc_idToken', tokens.idToken);
  if (keycloak.refreshToken) localStorage.setItem('kc_refreshToken', keycloak.refreshToken);
};

// Utility: clear stored tokens (referenced in event handler)
const clearStoredTokens = () => {
  localStorage.removeItem('kc_token');
  localStorage.removeItem('kc_refreshToken');
  localStorage.removeItem('kc_idToken');
};

// Handle Keycloak initialization errors (e.g., expired tokens on refresh)
const handleKeycloakEvent = (event, error) => {
  const handleRefreshFailure = () => {
    // Rely on reload to pick up valid tokens from other tabs or force a re-init.
    // We avoid clearing tokens here to prevent triggering the 'storage' listener in other tabs.
    window.location.reload();
  };

  switch (event) {
  case 'onInitError':
  case 'onAuthError':
    // console.error('[Keycloak] Auth/init error:', error);
    // If we tried check-sso and failed, avoid future silent attempts (likely 3rd-party cookie blocked)
    // Don't persist this flag when on a public route — check-sso failures are expected for logged-out users there.
    if (initOptions.onLoad === 'check-sso' && !isPublicRoute) {
      localStorage.setItem('kc_disableCheckSso', 'true');
    }
    // Do NOT clear tokens here. Just force a login redirect.
    // If we clear tokens, we might kill a valid session in another tab due to a race or temporary glitch.
    if (keycloak && !keycloak.authenticated && !isPublicRoute) {
      keycloak.login({ redirectUri: window.location.origin });
    }
    break;
  case 'onTokenExpired':
    // Token expired; attempt silent refresh, fall back to full login if it fails.
    keycloak.updateToken(0)
      .then((refreshed) => {
        if (!refreshed) {
          // If not refreshed, treat like refresh error.
          handleRefreshFailure();
        }
      })
      .catch((err) => {
        // console.warn('[Keycloak] updateToken failed after expiry:', err);
        handleRefreshFailure();
      });
    break;
  case 'onAuthRefreshError':
    // Refresh (POST /protocol/openid-connect/token) returned 400 or failed.
    // console.warn('[Keycloak] Refresh error (likely 400 Bad Request):', error);
    handleRefreshFailure();
    break;
  case 'onAuthLogout':
    // Explicit logout should always clear tokens.
    clearStoredTokens();
    break;
  default:
    break;
  }
};

// Cross-tab logout/token update synchronization (optional enhancement)
window.addEventListener('storage', (e) => {
  if (e.key === 'kc_token' && e.newValue == null) {
    try {
      keycloak.logout({ redirectUri: window.location.origin });
    } catch (err) {
      // ignore logout errors (e.g., already logged out)
    }
  }
});

const root = ReactDOM.createRoot(document.getElementById('root'));

// Public routes (e.g. password reset) must render without any Keycloak initialization.
// Keycloak's check-sso and login-required both redirect to the IdP, which prevents
// anonymous pages from loading. Skip the provider entirely for those paths.
if (isPublicRoute) {
  root.render(
    <Provider store={store}>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </Provider>
  );
} else {
  root.render(
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={initOptions}
      onTokens={handleTokens}
      onEvent={handleKeycloakEvent}
      autoRefreshToken={false} // we implement manual refresh logic in KeycloakAuth component
    >
      <Provider store={store}>
        <HelmetProvider>
          <App />
        </HelmetProvider>
      </Provider>
    </ReactKeycloakProvider>
  );
}
